import React from 'react'
import { Box, Typography } from '@mui/material'
import { IconTile } from '../UI/Depth'
import { restaurantSidebarIcons } from '../Sidebar/sidebarIcons'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import routes from '../../routes'

/**
 * Page heading shown at the top of every view: the page title and an optional
 * one-line description. The title is derived from the active route so existing
 * views can keep rendering `<Header />` with no props, while a view that wants
 * its own wording can pass `title` / `subtitle`. `icon` adds a tinted tile in
 * front of the title.
 * `action` places a control (a button, say) at the far end of the heading row.
 */
function Header({ title, subtitle, icon, action }) {
  const location = useLocation()
  const { t } = useTranslation()

  // `routes` imports the views, which import this component back, so guard
  // against the binding not being ready during module initialisation.
  const activeRoute = (routes || []).find(
    route =>
      route.path &&
      location.pathname.indexOf(`${route.layout}${route.path}`) !== -1
  )

  const heading =
    title ||
    (activeRoute
      ? activeRoute.name === 'Overview'
        ? 'Marketplace Overview'
        : t(activeRoute.label || activeRoute.name)
      : '')
  // Per-page description, keyed off the route name. Pages without a
  // `Subtitle<RouteName>` string simply render the title on its own.
  let description = subtitle
  if (!description && activeRoute) {
    const key = `Subtitle${activeRoute.name.replace(/\s/g, '')}`
    const translated = t(key)
    if (translated !== key) description = translated
  }

  // Store pages get their sidebar icon as a raised tile beside the title.
  const Icon =
    icon ||
    (activeRoute && activeRoute.layout === '/admin'
      ? restaurantSidebarIcons[activeRoute.name]
      : null)

  if (!heading) return null

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: { xs: 2, sm: 0 },
        pt: { xs: 1, sm: 0 },
        pb: 3,
        flexWrap: 'wrap'
      }}>
      {Icon ? (
        <IconTile
          icon={Icon}
          size={52}
          sx={{ display: { xs: 'none', sm: 'flex' } }}
        />
      ) : null}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="h1"
          sx={{
            color: 'text.primary',
            fontSize: { xs: 24, sm: 28 },
            fontWeight: 800,
            letterSpacing: '-0.02em'
          }}>
          {heading}
        </Typography>
        {description ? (
          <Typography
            variant="body1"
            sx={{
              color: 'text.disabled',
              mt: 0.75,
              fontSize: 14
            }}>
            {description}
          </Typography>
        ) : null}
      </Box>
      {action ? <Box sx={{ ml: 'auto', flexShrink: 0 }}>{action}</Box> : null}
    </Box>
  )
}

export default Header
